import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Result } from '../models/core/result.model';
import { AssetCode } from '../models/common/asset-code.model';
import {
  CatalogAssetCodesGroupDistinctCreate,
  CatalogAssetCodesGroupDistinctResponse,
  CatalogAssetCodesGroupDistinctUpdate
} from '../models/common/asset-codes-group-distinct.model';
import {
  CatalogLccaPeriodicalsCreate,
  CatalogLccaPeriodicalsResponse,
  CatalogLccaPeriodicalsUpdate
} from '../models/common/lcca-periodical.model';
import {
  CatalogLccaTriggerCreate,
  CatalogLccaTriggerResponse,
  CatalogLccaTriggerUpdate
} from '../models/common/lcca-trigger.model';
import { CatalogLccaTypeCreate, CatalogLccaTypeResponse, CatalogLccaTypeUpdate } from '../models/common/lcca-type-model';
import { CatalogLccaUnitCreate, CatalogLccaUnitResponse, CatalogLccaUnitUpdate } from '../models/common/lcca_unit.model';
import { Lcca } from '../models/common/lcca.model';
import { Road } from '../models/common/road.model';
import { Bound } from '../models/common/bound.model';
import { Zone } from '../models/common/zone.model';

@Injectable({
  providedIn: 'root'
})
export class CommonService {
  private baseUrl = `${environment.apiUrl}/common`;

  private http$ = inject(HttpClient);

  // CatalogAssetCodes
  findAssetCodes(page: number = 1, size: number = 10, keywords: string = ''): Observable<Result<AssetCode>> {
    let params = new HttpParams()
    .set('page', page)
    .set('size', size);

    if (keywords) {
      params = params.set('keywords', keywords);
    }

    return this.http$.get<Result<AssetCode>>(`${this.baseUrl}/asset_codes`, { params });
  }

  getAssetCodeById(id: number): Observable<AssetCode> {
    return this.http$.get<AssetCode>(`${this.baseUrl}/asset_codes/${id}`);
  }

  createAssetCode(item: AssetCode): Observable<AssetCode> {
    return this.http$.post<AssetCode>(`${this.baseUrl}/asset_codes`, item);
  }

  updateAssetCode(id: number, item: AssetCode): Observable<AssetCode> {
    return this.http$.put<AssetCode>(`${this.baseUrl}/asset_codes/${id}`, item);
  }

  deleteAssetCode(id: number): Observable<AssetCode> {
    return this.http$.delete<AssetCode>(`${this.baseUrl}/asset_codes/${id}`);
  }

  // CatalogAssetCodesGroupDistinct
  findAssetCodesGroupDistinct(page: number = 1, size: number = 10, keywords: string = ''): Observable<Result<CatalogAssetCodesGroupDistinctResponse>> {
    let params = new HttpParams()
      .set('page', page)
      .set('size', size);

    if (keywords) {
      params = params.set('keywords', keywords);
    }

    return this.http$.get<Result<CatalogAssetCodesGroupDistinctResponse>>(`${this.baseUrl}/asset_codes_group_distinct`, { params });
  }

  getAssetCodesGroupDistinctById(id: number): Observable<CatalogAssetCodesGroupDistinctResponse> {
    return this.http$.get<CatalogAssetCodesGroupDistinctResponse>(`${this.baseUrl}/asset_codes_group_distinct/${id}`);
  }

  createAssetCodesGroupDistinct(item: CatalogAssetCodesGroupDistinctCreate): Observable<CatalogAssetCodesGroupDistinctResponse> {
    return this.http$.post<CatalogAssetCodesGroupDistinctResponse>(`${this.baseUrl}/asset_codes_group_distinct`, item);
  }

  updateAssetCodesGroupDistinct(id: number, item: CatalogAssetCodesGroupDistinctUpdate): Observable<CatalogAssetCodesGroupDistinctResponse> {
    return this.http$.put<CatalogAssetCodesGroupDistinctResponse>(`${this.baseUrl}/asset_codes_group_distinct/${id}`, item);
  }

  deleteAssetCodesGroupDistinct(id: number): Observable<CatalogAssetCodesGroupDistinctResponse> {
    return this.http$.delete<CatalogAssetCodesGroupDistinctResponse>(`${this.baseUrl}/asset_codes_group_distinct/${id}`);
  }

  // CatalogBounds
  findBounds(page: number = 1, size: number = 10, keywords: string = ''): Observable<Result<Bound>> {
    let params = new HttpParams()
      .set('page', page)
      .set('size', size);

    if (keywords) {
      params = params.set('keywords', keywords);
    }

    return this.http$.get<Result<Bound>>(`${this.baseUrl}/bounds`, { params });
  }

  getBoundById(id: number): Observable<Bound> {
    return this.http$.get<Bound>(`${this.baseUrl}/bounds/${id}`);
  }

  createBound(item: Bound): Observable<Bound> {
    return this.http$.post<Bound>(`${this.baseUrl}/bounds`, item);
  }

  updateBound(id: number, item: Bound): Observable<Bound> {
    return this.http$.put<Bound>(`${this.baseUrl}/bounds/${id}`, item);
  }

  deleteBound(id: number): Observable<Bound> {
    return this.http$.delete<Bound>(`${this.baseUrl}/bounds/${id}`);
  }

  // CatalogLccaPeriodicals
  findLccaPeriodicals(page: number = 1, size: number = 10, keywords: string = ''): Observable<Result<CatalogLccaPeriodicalsResponse>> {
    let params = new HttpParams()
      .set('page', page)
      .set('size', size);

    if (keywords) {
      params = params.set('keywords', keywords);
    }

    return this.http$.get<Result<CatalogLccaPeriodicalsResponse>>(`${this.baseUrl}/lcca_periodicals`, { params });
  }

  getLccaPeriodicalById(id: number): Observable<CatalogLccaPeriodicalsResponse> {
    return this.http$.get<CatalogLccaPeriodicalsResponse>(`${this.baseUrl}/lcca_periodicals/${id}`);
  }

  createLccaPeriodical(item: CatalogLccaPeriodicalsCreate): Observable<CatalogLccaPeriodicalsResponse> {
    return this.http$.post<CatalogLccaPeriodicalsResponse>(`${this.baseUrl}/lcca_periodicals`, item);
  }

  updateLccaPeriodical(id: number, item: CatalogLccaPeriodicalsUpdate): Observable<CatalogLccaPeriodicalsResponse> {
    return this.http$.put<CatalogLccaPeriodicalsResponse>(`${this.baseUrl}/lcca_periodicals/${id}`, item);
  }

  deleteLccaPeriodical(id: number): Observable<CatalogLccaPeriodicalsResponse> {
    return this.http$.delete<CatalogLccaPeriodicalsResponse>(`${this.baseUrl}/lcca_periodicals/${id}`);
  }

  // CatalogLccaTrigger
  findLccaTriggers(page: number = 1, size: number = 10, keywords: string = ''): Observable<Result<CatalogLccaTriggerResponse>> {
    let params = new HttpParams()
      .set('page', page)
      .set('size', size);

    if (keywords) {
      params = params.set('keywords', keywords);
    }

    return this.http$.get<Result<CatalogLccaTriggerResponse>>(`${this.baseUrl}/lcca_trigger`, { params });
  }

  getLccaTriggerById(id: number): Observable<CatalogLccaTriggerResponse> {
    return this.http$.get<CatalogLccaTriggerResponse>(`${this.baseUrl}/lcca_trigger/${id}`);
  }

  createLccaTrigger(item: CatalogLccaTriggerCreate): Observable<CatalogLccaTriggerResponse> {
    return this.http$.post<CatalogLccaTriggerResponse>(`${this.baseUrl}/lcca_trigger`, item);
  }

  updateLccaTrigger(id: number, item: CatalogLccaTriggerUpdate): Observable<CatalogLccaTriggerResponse> {
    return this.http$.put<CatalogLccaTriggerResponse>(`${this.baseUrl}/lcca_trigger/${id}`, item);
  }

  deleteLccaTrigger(id: number): Observable<CatalogLccaTriggerResponse> {
    return this.http$.delete<CatalogLccaTriggerResponse>(`${this.baseUrl}/lcca_trigger/${id}`);
  }

  // CatalogLccaType
  findLccaTypes(page: number = 1, size: number = 10, keywords: string = ''): Observable<Result<CatalogLccaTypeResponse>> {
    let params = new HttpParams()
      .set('page', page)
      .set('size', size);

    if (keywords) {
      params = params.set('keywords', keywords);
    }

    return this.http$.get<Result<CatalogLccaTypeResponse>>(`${this.baseUrl}/lcca_type`, { params });
  }

  getLccaTypeById(id: number): Observable<CatalogLccaTypeResponse> {
    return this.http$.get<CatalogLccaTypeResponse>(`${this.baseUrl}/lcca_type/${id}`);
  }

  createLccaType(item: CatalogLccaTypeCreate): Observable<CatalogLccaTypeResponse> {
    return this.http$.post<CatalogLccaTypeResponse>(`${this.baseUrl}/lcca_type`, item);
  }

  updateLccaType(id: number, item: CatalogLccaTypeUpdate): Observable<CatalogLccaTypeResponse> {
    return this.http$.put<CatalogLccaTypeResponse>(`${this.baseUrl}/lcca_type/${id}`, item);
  }

  deleteLccaType(id: number): Observable<CatalogLccaTypeResponse> {
    return this.http$.delete<CatalogLccaTypeResponse>(`${this.baseUrl}/lcca_type/${id}`);
  }

  // CatalogLccaUnit
  findLccaUnits(page: number = 1, size: number = 10, keywords: string = ''): Observable<Result<CatalogLccaUnitResponse>> {
    let params = new HttpParams()
      .set('page', page)
      .set('size', size);

    if (keywords) {
      params = params.set('keywords', keywords);
    }

    return this.http$.get<Result<CatalogLccaUnitResponse>>(`${this.baseUrl}/lcca_unit`, { params });
  }

  getLccaUnitById(id: number): Observable<CatalogLccaUnitResponse> {
    return this.http$.get<CatalogLccaUnitResponse>(`${this.baseUrl}/lcca_unit/${id}`);
  }

  createLccaUnit(item: CatalogLccaUnitCreate): Observable<CatalogLccaUnitResponse> {
    return this.http$.post<CatalogLccaUnitResponse>(`${this.baseUrl}/lcca_unit`, item);
  }

  updateLccaUnit(id: number, item: CatalogLccaUnitUpdate): Observable<CatalogLccaUnitResponse> {
    return this.http$.put<CatalogLccaUnitResponse>(`${this.baseUrl}/lcca_unit/${id}`, item);
  }

  deleteLccaUnit(id: number): Observable<CatalogLccaUnitResponse> {
    return this.http$.delete<CatalogLccaUnitResponse>(`${this.baseUrl}/lcca_unit/${id}`);
  }

  // Lcca
  findLccas(page: number = 1, size: number = 10, keywords: string = ''): Observable<Result<Lcca>> {
    let params = new HttpParams()
      .set('page', page)
      .set('size', size);

    if (keywords) {
      params = params.set('keywords', keywords);
    }

    return this.http$.get<Result<Lcca>>(`${this.baseUrl}/lcca`, { params });
  }

  getLccaById(id: number): Observable<Lcca> {
    return this.http$.get<Lcca>(`${this.baseUrl}/lcca/${id}`);
  }

  createLcca(item: Lcca): Observable<Lcca> {
    return this.http$.post<Lcca>(`${this.baseUrl}/lcca`, item);
  }

  updateLcca(id: number, item: Lcca): Observable<Lcca> {
    return this.http$.put<Lcca>(`${this.baseUrl}/lcca/${id}`, item);
  }

  deleteLcca(id: number): Observable<Lcca> {
    return this.http$.delete<Lcca>(`${this.baseUrl}/lcca/${id}`);
  }

  // CatalogRoads
  findRoads(page: number = 1, size: number = 10, keywords: string = ''): Observable<Result<Road>> {
    let params = new HttpParams()
      .set('page', page)
      .set('size', size);

    if (keywords) {
      params = params.set('keywords', keywords);
    }

    return this.http$.get<Result<Road>>(`${this.baseUrl}/roads`, { params });
  }

  getRoadById(id: number): Observable<Road> {
    return this.http$.get<Road>(`${this.baseUrl}/roads/${id}`);
  }

  createRoad(item: Road): Observable<Road> {
    return this.http$.post<Road>(`${this.baseUrl}/roads`, item);
  }

  updateRoad(id: number, item: Road): Observable<Road> {
    return this.http$.put<Road>(`${this.baseUrl}/roads/${id}`, item);
  }

  deleteRoad(id: number): Observable<Road> {
    return this.http$.delete<Road>(`${this.baseUrl}/roads/${id}`);
  }

  // Zone
  findZones(page: number = 1, size: number = 10, keywords: string = ''): Observable<Result<Zone>> {
    let params = new HttpParams()
      .set('page', page)
      .set('size', size);

    if (keywords) {
      params = params.set('keywords', keywords);
    }

    return this.http$.get<Result<Zone>>(`${this.baseUrl}/zones`, { params });
  }

  getZoneById(id: number): Observable<Zone> {
    return this.http$.get<Zone>(`${this.baseUrl}/zones/${id}`);
  }

  createZone(item: Zone): Observable<Zone> {
    return this.http$.post<Zone>(`${this.baseUrl}/zones`, item);
  }

  updateZone(id: number, item: Zone): Observable<Zone> {
    return this.http$.put<Zone>(`${this.baseUrl}/zones/${id}`, item);
  }

  deleteZone(id: number): Observable<Zone> {
    return this.http$.delete<Zone>(`${this.baseUrl}/zones/${id}`);
  }
}
